import React, { useEffect, useMemo, useState } from "react";

import { Box, Grid, InputAdornment } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { Input, PhoneInput, AutoSuggest, Checkbox } from "Components/Form";
import Skeleton from "Components/Generic/Skeleton";
import { useAppState } from "Context/StateContext";
import useYupValidation from "Hooks/useYupValidation";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import { ValidationError } from "yup";

import { useShipmentContext } from "../../../hooks/useContext";
import { ContactTypeEnum } from "../enum/WaybillNofitications.enum";
import { IFormValues } from "../interface/WaybillNotifications.interface";
import WaybillNotificationService from "../WaybillNotifications.service";

interface ShipmentNewSubscriberFormProps {
  values: IFormValues | any;
  onChange: (values: Partial<IFormValues>) => void;
  errors: ValidationError[];
}

const contactTypeOptions = [
  { id: ContactTypeEnum.NewContact, label: ContactTypeEnum.NewContact },
  {
    id: ContactTypeEnum.User,
    label: ContactTypeEnum.User,
  },
];

const ShipmentNewSubscriberForm = ({
  values,
  onChange,
  errors,
}: ShipmentNewSubscriberFormProps) => {
  const { fieldHasError, getErrorByField } = useYupValidation(errors || []);
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { shipment } = useShipmentContext();
  const initialValues = useMemo(() => values, [values]);

  const shipmentOrders = useMemo(() => {
    const jobs =
      shipment?.jobs?.map((job) => ({
        id: job?.reference?.toString(),
        label: job?.reference!,
        value: job?.reference!,
      })) || [];

    const customerWithOrders =
      shipment?.customersToShipment?.map((ctj) => ({
        id: ctj.order!,
        label: ctj.order!,
        value: ctj.order!,
      })) || [];

    return [...jobs, ...customerWithOrders];
  }, [shipment?.jobs, shipment?.customersToShipment]);

  const [selectedContactType, setSelectedContactType] = useState<any>();
  const [selectedUser, setSelectedUser] = useState<IFormValues | undefined>(initialValues);
  const [selectedOrder, setSelectedOrder] = useState<any>();
  const [notifyVia, setNotifyVia] = useState({
    email: initialValues?.notifyViaEmail,
    sms: initialValues?.notifyViaSms,
  });
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const [isEditView, setIsEditView] = useState<boolean>(!!initialValues?.id);
  const [isDisableCheckBoxEmail, setIsDisableCheckboxEmail] = useState<boolean>(false);
  const [isDisableCheckBoxSMS, setIsDisableCheckboxSMS] = useState<boolean>(false);
  const { data: companyUsers, isLoading: isLoadingCompanyUsers } = useQuery({
    queryKey: ["shipment.notifications.companyUsers", currentUser?.companyId],
    queryFn: () => WaybillNotificationService.getUsersByCompanyId(currentUser?.companyId || 0),
    enabled: !!currentUser?.companyId,
    onSuccess: (companyUsers: any[]) => {
      const matchedUser = companyUsers?.find(
        (user) =>
          String(user?.userId) === String(initialValues?.userId) ||
          String(user?.userId) === String(initialValues?.managerId) ||
          String(user?.userId) === String(initialValues?.bookingAgentId) ||
          String(user?.userId) === String(initialValues?.consultantId) ||
          String(user?.userId) === String(initialValues?.moveCoordinatorId)
      );
      if (!_.isEmpty(matchedUser)) {
        setSelectedUser({ ...selectedUser, ...matchedUser } as any);
      }
    },
    select: (response) =>
      response?.data?.map((user: any) => {
        const mappedUser =
          user?.usersCompanies && user?.usersCompanies.length > 0
            ? user?.usersCompanies[0]
            : undefined;

        return {
          ...user,
          label: user?.fullName || user?.username,
          mobile: user?.phone,
          userId: mappedUser?.userId,
        };
      }) || [],
  });

  const emitTheChanges = () => {
    const changes = {
      ...selectedUser,
      id: initialValues?.id || null,
      firstName: selectedUser?.firstName || selectedUser?.name,
      lastName: selectedUser?.lastName || "",
      email: selectedUser?.email || "",
      jobReference: selectedOrder?.value,
      notifyViaEmail: notifyVia?.email,
      notifyViaSms: notifyVia?.sms,
      contactType:
        selectedUser?.contactType || selectedUser?.typeUser || selectedContactType?.label,
      mobile: selectedUser?.mobile || "",
    };
    onChange(changes);
  };

  const isDisableEmail = (selectedTypeId?: string, dataUser?: any) => {
    const id = selectedTypeId || selectedContactType?.id;
    const dataSelectedUser = dataUser || selectedUser;

    if (isEditView) return false;
    if (!isEditView && (id !== ContactTypeEnum.User || dataSelectedUser?.email)) return false;

    return true;
  };

  const isDisableSMS = (selectedTypeId?: string, dataUser?: any) => {
    const id = selectedTypeId || selectedContactType?.id;
    const dataSelectedUser = dataUser || selectedUser;

    if (isEditView) return false;
    if (!isEditView && (id !== ContactTypeEnum.User || dataSelectedUser?.mobile)) return false;

    return true;
  };
  const handleUserSelect = (user: any) => {
    const checkDisableEmail = isDisableEmail(selectedContactType?.id, user);
    const checkDisableSms = isDisableSMS(selectedContactType?.id, user);

    setIsDisableCheckboxEmail(checkDisableEmail);
    setIsDisableCheckboxSMS(checkDisableSms);
    setNotifyVia({
      email: false,
      sms: false,
    });
    if (!_.isEmpty(user)) {
      setSelectedUser({
        ...user,
        notifyVia: [],
      });
    }
  };

  const handleTypeOfContactSelect = (value: { id: string; label: string }) => {
    setNotifyVia({
      email: false,
      sms: false,
    });
    const selectedType = contactTypeOptions.find((opt) => String(opt?.id) === String(value?.id));

    if (selectedType) {
      const checkDisableEmail = isDisableEmail(selectedType?.id, selectedUser);
      const checkDisableSms = isDisableSMS(selectedType?.id, selectedUser);

      setIsDisableCheckboxEmail(checkDisableEmail);
      setIsDisableCheckboxSMS(checkDisableSms);
      setSelectedContactType(selectedType);
    }

    setSelectedUser(undefined);
  };

  useEffect(() => {
    emitTheChanges();
  }, [selectedUser, selectedOrder, notifyVia]);

  useEffect(() => {
    if (initialValues) {
      const checkDisableEmail = isDisableEmail();
      const checkDisableSms = isDisableSMS();

      setIsDisableCheckboxEmail(checkDisableEmail);
      setIsDisableCheckboxSMS(checkDisableSms);
    }
  }, [initialValues]);

  useEffect(() => {
    if (selectedOrder) return;
    if (initialValues?.jobReference && shipmentOrders.length > 0) {
      const matchedOrder = shipmentOrders.find(
        (opt) => String(opt.id) === String(initialValues.jobReference)
      );
      setSelectedOrder(matchedOrder as any);
    }
  }, [selectedOrder, initialValues?.jobReference, shipmentOrders]);

  useEffect(() => {
    if (selectedContactType) return;

    const hasLinkedUser =
      values?.userId ||
      values?.managerId ||
      values?.bookingAgentId ||
      values?.moveCoordinatorId ||
      values?.consultantId;

    setSelectedContactType(hasLinkedUser ? contactTypeOptions[1] : contactTypeOptions[0]);
  }, [
    selectedContactType,
    values?.userId,
    values?.managerId,
    values?.bookingAgentId,
    values?.moveCoordinatorId,
    values?.consultantId,
  ]);

  return (
    <Box component="form" noValidate key={initialValues?.id}>
      <Grid container spacing={"20px"}>
        <Grid item xs={12}>
          <AutoSuggest
            label={translate("typeOfContact")}
            placeholder={translate("enterTypeOfContact")}
            value={selectedContactType ?? null}
            inputValue={selectedContactType?.label ?? ""}
            disabled={!!initialValues?.id}
            error={fieldHasError("contactType")}
            errorMessage={getErrorByField("contactType")}
            onChange={handleTypeOfContactSelect}
            options={contactTypeOptions}
            required
            getOptionLabel={(option: any) => option?.label || option?.fullName || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              String(option?.id) === String(value?.id)
            }
            getOptionSelected={(option: any, value: any) =>
              String(option?.id) === String(value?.id)
            }
          />
        </Grid>
        {!!selectedContactType && selectedContactType.id === ContactTypeEnum.NewContact && (
          <Grid item xs={12}>
            <Input
              label={translate("enterContactType")}
              disabled={!!initialValues?.id || !!selectedUser?.userId}
              placeholder={translate("enterContactType")}
              name="contactTypeInput"
              error={fieldHasError("contactTypeInput")}
              errorMessage={getErrorByField("contactTypeInput")}
              value={selectedUser?.typeUser || values?.contactType}
              onChange={(e) => {
                setSelectedUser((prev: any) => ({
                  ...(prev || {}),
                  typeUser: e?.target?.value,
                }));
              }}
              required
            />
          </Grid>
        )}
        {!!selectedContactType && selectedContactType.id === ContactTypeEnum.User && (
          <Grid item xs={12}>
            <Skeleton isLoading={isLoadingCompanyUsers}>
              <AutoSuggest
                placeholder={translate("searchForAnUserByTyping")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FiSearch size={20} color={colors.gray500} />
                    </InputAdornment>
                  ),
                }}
                disabled={!!initialValues?.id}
                value={selectedUser}
                onChange={handleUserSelect}
                options={companyUsers as any}
                required
                inputValue={selectedUser?.fullName || selectedUser?.name || ""}
                getOptionLabel={(option: any) => option?.label}
                getOptionSelected={(option: any, value: any) => {
                  return String(option?.userId) === String(value?.userId);
                }}
              />
            </Skeleton>
          </Grid>
        )}
        <Grid item xs={12}>
          <AutoSuggest
            label={`${translate("order")} #`}
            placeholder={`${translate("enterOrder")}#`}
            error={fieldHasError("jobReference")}
            errorMessage={getErrorByField("jobReference")}
            disabled={!!initialValues?.id}
            value={selectedOrder ?? null}
            inputValue={selectedOrder?.label ?? ""}
            onChange={(value) => {
              const selected = shipmentOrders.find((opt) => String(opt.id) === String(value?.id));
              setSelectedOrder(selected || null);
            }}
            options={shipmentOrders}
            required
            getOptionLabel={(option: any) => option?.label || ""}
            isOptionEqualToValue={(option: any, value: any) =>
              String(option?.id) === String(value?.id)
            }
            getOptionSelected={(option: any, value: any) =>
              String(option?.id) === String(value?.id)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label={`${translate("firstName")} #`}
            placeholder={translate("enterFirstName")}
            error={fieldHasError("firstName")}
            errorMessage={getErrorByField("firstName")}
            name="firstName"
            disabled={
              !!initialValues?.id ||
              !!selectedUser?.userId ||
              !!selectedUser?.bookingAgentId ||
              !!selectedUser?.managerId ||
              !!selectedUser?.moveCoordinatorId ||
              !!selectedUser?.consultantId
            }
            value={selectedUser?.firstName || selectedUser?.name}
            onChange={(e) => {
              setSelectedUser((prev) => {
                if (!prev)
                  return {
                    firstName: e.target.value,
                    name: e.target.value,
                    notifyVia: [],
                  };

                return {
                  ...prev,
                  firstName: e.target.value,
                  name: e.target.value,
                  notifyVia: prev.notifyVia || [],
                };
              });
            }}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            label={`${translate("lastName")} #`}
            disabled={
              !!initialValues?.id ||
              !!selectedUser?.userId ||
              !!selectedUser?.bookingAgentId ||
              !!selectedUser?.managerId ||
              !!selectedUser?.moveCoordinatorId ||
              !!selectedUser?.consultantId
            }
            placeholder={translate("enterLastName")}
            name="lastName"
            value={selectedUser?.lastName}
            onChange={(e) => {
              if (!selectedUser) {
                setSelectedUser({
                  lastName: e.target.value,
                  notifyVia: [],
                });
              } else {
                setSelectedUser({
                  ...selectedUser,
                  lastName: e.target.value,
                  notifyVia: selectedUser.notifyVia || [],
                });
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box>
            <div
              style={{
                color: colors.brandBlue600,
                fontFamily: "Manrope",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "20px" /* 142.857% */,
                letterSpacing: "0.1px",
                marginBottom: "4px",
              }}
            >
              {translate("notifyVia")}
            </div>
            <Box sx={{ display: "flex", gap: "50px" }}>
              <Checkbox
                label="E-mail"
                disabled={isDisableCheckBoxEmail}
                checked={notifyVia?.email}
                onChange={(_, checked) => {
                  setNotifyVia({ ...notifyVia, email: checked });
                }}
              />
              <Checkbox
                label="SMS"
                disabled={isDisableCheckBoxSMS}
                checked={notifyVia?.sms}
                onChange={(_, checked) => {
                  setNotifyVia({ ...notifyVia, sms: checked });
                }}
              />
            </Box>
          </Box>
        </Grid>
        {notifyVia?.email && (
          <Grid item xs={12}>
            <Input
              disabled={
                !!selectedUser?.userId ||
                !!selectedUser?.bookingAgentId ||
                !!selectedUser?.managerId ||
                !!selectedUser?.moveCoordinatorId ||
                !!selectedUser?.consultantId
              }
              label={translate("email")}
              placeholder={translate("enterEmail")}
              error={fieldHasError("email")}
              errorMessage={getErrorByField("email")}
              name="email"
              type="email"
              value={selectedUser?.email}
              onChange={(e) => {
                if (!selectedUser) {
                  setSelectedUser({
                    email: e.target.value,
                    notifyVia: [],
                  });
                } else {
                  setSelectedUser({
                    ...selectedUser,
                    email: e.target.value,
                    notifyVia: selectedUser.notifyVia || [],
                  });
                }
              }}
              required
            />
          </Grid>
        )}
        {notifyVia.sms && (
          <Grid item xs={12}>
            <PhoneInput
              required
              name="mobile"
              defaultCountry={currentUser?.company?.currency?.code?.slice(0, 2)}
              label={translate("mobileNumber")}
              error={fieldHasError("mobile")}
              errorMessage={getErrorByField("mobile")}
              value={selectedUser?.mobile}
              disabled={
                !!selectedUser?.userId ||
                !!selectedUser?.bookingAgentId ||
                !!selectedUser?.managerId ||
                !!selectedUser?.moveCoordinatorId ||
                !!selectedUser?.consultantId
              }
              onChange={(e) => {
                setSelectedUser({
                  ...(selectedUser || {}),
                  mobile: e,
                  notifyVia: selectedUser?.notifyVia || [],
                });
              }}
              placeholder={translate("enterMobileNumber")}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ShipmentNewSubscriberForm;
